<template>
  <label :class="['full-width', labelClass]" @click="noOp">
    <div v-if="!noLabel && (inputLabel || hintLabel)">
      <span v-if="inputLabel" class="date-picker-label">{{ inputLabel }}</span>
      <span v-if="hintLabel" style="color: #626262"
        ><em>{{ hintLabel }}</em></span
      >
    </div>
    <div class="d-flex">
      <div
        v-if="buttons"
        class="arrow-cont"
        data-cy="previous-day-btn"
        @click="prevDay()"
      >
        <i class="far fa-chevron-left"></i>
      </div>
      <date-picker
        v-model:value="date"
        v-bind="$attrs"
        :format="format"
        :value-type="valueType"
        :placeholder="placeholderLabel"
        class="full-width"
        :range="range"
        :input-class="inputClassName"
        :popup-class="popupClasses"
        :editable="editable"
        :disabled="disabled"
        :input-attr="inputAttr"
        @change="checkClearingData"
        @blur="$emit('blur')"
        @pick="($evt) => $emit('pick', $evt)"
      >
        <template
          v-if="range && shortcuts && periods?.length"
          #footer="{ emit }"
        >
          <Shortcuts :emit="emit" :periods="periods" />
        </template>
        <template #header><slot name="header"></slot></template>
      </date-picker>
      <div
        v-if="buttons"
        class="arrow-cont"
        data-cy="next-day-btn"
        @click="nextDay()"
      >
        <i class="far fa-chevron-right"></i>
      </div>
    </div>
    <span
      v-if="findByName(validationErrors, name) || fieldErrorMessage"
      class="text-danger error-text"
      >{{ findByName(validationErrors, name) || fieldErrorMessage }}</span
    >
  </label>
</template>

<script>
import DatePicker from "vue-datepicker-next";
import { findByName } from "@/lib/vuelidate_helpers";

import CONSTANTS from "@/Constants.js";
import Shortcuts from "@/Reports/Shortcuts.vue";
import useVuelidate from "@vuelidate/core";
import { requiredIf } from "@vuelidate/validators";
import "vue-datepicker-next/index.css";

export default {
  components: {
    "date-picker": DatePicker,
    Shortcuts
  },
  props: {
    format: {
      type: String,
      default: CONSTANTS.DATE_FORMAT
    },
    labelClass: {
      type: String,
      default: ""
    },
    popupClass: {
      type: String,
      default: ""
    },
    name: {
      type: String,
      default: ""
    },
    noLabel: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: ""
    },
    periods: {
      type: Array,
      default: null
    },
    hintLabel: {
      type: String,
      default: ""
    },
    modelValue: {
      type: [String, Date, Array],
      default: null
    },
    valueType: {
      type: String,
      default: "format"
    },
    validationErrors: {
      type: Object,
      default() {
        return {
          first: () => {}
        };
      }
    },
    fieldError: {
      type: Boolean,
      default: false
    },
    fieldErrorMessage: {
      type: String,
      default: ""
    },
    range: {
      type: Boolean,
      default: false
    },
    shortcuts: {
      type: Boolean,
      default: false
    },
    buttons: {
      type: Boolean,
      default: false
    },
    label: {
      type: String,
      default: ""
    },
    editable: {
      type: Boolean,
      default: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    inputAttr: {
      type: Object,
      default: () => ({})
    },
    isRequired: {
      type: Boolean,
      default: false
    }
  },
  emits: ["update:modelValue", "pick", "clear-date", "blur"],
  setup(props) {
    return {
      v$: useVuelidate()
    };
  },
  computed: {
    date: {
      get() {
        let date = null;
        if (Array.isArray(this.modelValue)) {
          date = this.modelValue.map((date) => this.formatDate(date));
        } else {
          date = this.formatDate(this.modelValue);
        }
        return date;
      },
      set(val) {
        let date = null;
        if (Array.isArray(val)) {
          date = val.map((date) => this.parseDate(date));
        } else {
          date = this.parseDate(val);
        }
        this.$emit("update:modelValue", date);
      }
    },
    popupClasses() {
      return [this.popupClass, this.range ? "date-ranger-picker" : ""]
        .filter(Boolean)
        .join(" ");
    },
    inputLabel() {
      return this.label || this.name || this.placeholder;
    },
    inputClassName() {
      const errorFound = findByName(this.validationErrors, this.name);
      const hasError = errorFound || this.fieldError || this.fieldErrorMessage;
      const squared = this.buttons;
      return `form-control date-picker-input full-width ${
        hasError && "is-invalid"
      } ${squared && "squared"}`;
    },
    placeholderLabel() {
      return this.placeholder || this.name;
    }
  },
  validations() {
    return {
      form: {
        check_number: {
          requiredIf: requiredIf(this.isRequired),
          $autoDirty: true
        }
      }
    };
  },
  methods: {
    noOp(e) {
      // Prevent label click from opening calendar
      // Calendar should only open on input click
      e.preventDefault();
    },
    parseDate(val) {
      return val ? moment(val, this.format).toISOString() : val;
    },
    // TODO: We should have the datepicker only support ISO strings moving forward
    formatDate(val) {
      if (moment(val, moment.ISO_8601).isValid()) {
        return moment(val).format(this.format);
      }
      return moment(val, "MM/DD/YYYY").format(this.format);
    },
    checkClearingData(date) {
      if (!date || (!date[0] && !date[1])) {
        this.$emit("clear-date");
      }
    },
    prevDay() {
      this.date = moment(this.date, this.format)
        .subtract(1, "days")
        .format(this.format);
    },
    nextDay() {
      this.date = moment(this.date, this.format)
        .add(1, "days")
        .format(this.format);
    },
    findByName
  }
};
</script>

<style lang="scss">
.date-ranger-picker.mx-datepicker-main {
  .mx-datepicker-content {
    display: flex;
    .mx-calendar-range {
      order: 2;
    }
    .mx-datepicker-footer {
      order: 1;
      border-right: 1px solid #e8e8e8;
      .shortcuts {
        flex-direction: column;
      }
    }
  }
}
.mx-datepicker {
  .form-control[readonly] {
    background-color: #fff !important;
  }
}
.mx-calendar-content .cell.active {
  background-color: var(--primary-color);
}
.mx-icon-calendar {
  right: 6px;
}
.date-picker-label {
  font-weight: bold;
  margin-left: 2px;
  margin-bottom: 3px;
  display: inline-block;
  min-height: 15px;
}
.date-picker-input {
  padding-right: 20px;
}
.arrow-cont {
  background-color: #fff;
  padding: 10px 15px;
  box-shadow: var(--box-shadow);
  height: 36px;
  border: 1.5px solid var(--sera-color-fields-border, #9ca4aa);
  color: var(--primary-color);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover {
    background-color: var(--primary-color);
    color: #fff;
  }
  &:first-of-type {
    border-radius: 3px 0 0 3px;
  }

  &:last-of-type {
    border-radius: 0 3px 3px 0;
  }
}
.squared {
  border-radius: 0 !important;
  border-left: none !important;
  border-right: none !important;
}
</style>
